import { filterEntriesByDate } from './dateFilters'
import { aggregateMetrics } from '@petrotrack/shared'

/** Branches visible to the current user after role + filter constraints. */
export function getVisibleBranches(currentUser, branches, filters) {
  let visible = [...branches]
  if (currentUser.role === 'manager' || currentUser.role === 'staff') {
    visible = visible.filter((b) => b.id === currentUser.branchId)
  } else if (currentUser.role === 'admin') {
    visible = visible.filter((b) => b.organizationId === currentUser.organizationId)
  }
  if (currentUser.role === 'superadmin' && filters.selectedOrg !== 'all') {
    visible = visible.filter((b) => b.organizationId === filters.selectedOrg)
  }
  if (filters.selectedBranch !== 'all') {
    visible = visible.filter((b) => b.id === filters.selectedBranch)
  }
  return visible
}

/** Distinct products at a branch (from its tanks + pumps), respecting the fuel filter. */
export function getBranchProducts(branch, filters) {
  const set = new Set()
  ;(branch.tanks || []).forEach((t) => set.add(t.product))
  ;(branch.pumps || []).forEach((p) => set.add(p.product))
  let products = [...set].sort()
  if (filters.fuelTypeFilter && filters.fuelTypeFilter !== 'all') products = products.filter((p) => p === filters.fuelTypeFilter)
  return products
}

/** Product-day entries for the given branches, filtered by product + date. */
export function getFilteredEntries(entries, visibleBranchIds, filters) {
  return filterEntriesByDate(
    entries.filter((e) => visibleBranchIds.has(e.branchId) && (filters.fuelTypeFilter === 'all' || e.product === filters.fuelTypeFilter)),
    filters.dateFilter, filters.customRange,
  )
}

/** Station/day expenses for the given branches, filtered by date. */
export function getFilteredExpenses(expenses, visibleBranchIds, filters) {
  return filterEntriesByDate(expenses.filter((x) => visibleBranchIds.has(x.branchId)), filters.dateFilter, filters.customRange)
}

/** Aggregate metrics across visible branches for the current filters (entries + expenses). */
export function computeDashboardMetrics(currentUser, branches, entries, expenses, filters) {
  const visible = getVisibleBranches(currentUser, branches, filters)
  const ids = new Set(visible.map((b) => b.id))
  const e = getFilteredEntries(entries, ids, filters)
  // Expenses are station-level — only meaningful when not filtered to one product.
  const x = filters.fuelTypeFilter === 'all' ? getFilteredExpenses(expenses, ids, filters) : []
  return aggregateMetrics(e, x)
}

export function getSelectableBranches(currentUser, branches, selectedOrg) {
  if (currentUser.role === 'manager' || currentUser.role === 'staff') return []
  if (currentUser.role === 'admin') return branches.filter((b) => b.organizationId === currentUser.organizationId)
  if (selectedOrg !== 'all') return branches.filter((b) => b.organizationId === selectedOrg)
  return branches
}

/**
 * Each tank's most recent measured level, from the daily entries already in
 * memory — there is no stock column on `tanks`, the dip readings are the
 * truth. Entries arrive newest-first, so the first row naming a tank wins.
 *
 * A day that has only been opened (`morning_open`) has no closing dip yet, so
 * fall back to what was in the tank that morning plus anything delivered since.
 * Tanks that have never been dipped are simply absent from the map — the
 * caller shows "no readings yet" rather than a misleading 0%.
 *
 * @returns {Map<string, {litres: number, date: string, source: 'closing'|'opening'}>}
 */
export function latestTankLevels(entries = []) {
  const out = new Map()
  for (const entry of entries) {
    for (const t of entry.tanks || []) {
      if (out.has(t.tankId)) continue
      if (t.closingLevel != null) {
        out.set(t.tankId, { litres: t.closingLevel, date: entry.date, source: 'closing' })
      } else if (t.openingLevel != null) {
        out.set(t.tankId, { litres: t.openingLevel + (t.supplyReceived || 0), date: entry.date, source: 'opening' })
      }
    }
  }
  return out
}

/** Fill fraction 0–1 for a tank, or null when it has never been dipped. */
export function tankFillFraction(tank, levels) {
  const reading = levels?.get(tank.id)
  if (!reading || !tank.capacity) return null
  return Math.max(0, Math.min(1, reading.litres / tank.capacity))
}
